/**
 * Asset Settings (UI-14)
 *
 * Capitalization threshold + category useful life configuration (BE-18). Changing them is the TC admin's;
 * a new useful life applies to assets bought afterwards.
 * Route: /settings/asset-config (also Settings › Finance › Fixed Assets)
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Alert, useTheme,
} from '@mui/material';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { serverMessage } from '../../utils/apiConvert';
import { fixedAssetService } from '../../services/accounting/fixedAsset.service';

const AssetSettings = ({ currentUser }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const canEdit = currentUser?.role === 'TC_ADMIN';

  const [threshold, setThreshold] = useState('');
  const [categories, setCategories] = useState([]);
  const [saved, setSaved] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const apply = (data) => {
    setThreshold(String(Number(data.capitalizationThreshold)));
    setCategories(data.categories.map((c) => ({ ...c })));
    setSaved(data);
  };

  useEffect(() => {
    fixedAssetService.getConfig()
      .then((res) => { if (res?.data) apply(res.data); })
      .catch((err) => setError(serverMessage(err, 'Failed to load asset settings')))
      .finally(() => setLoading(false));
  }, []);

  const updateCatLife = (key, years) => {
    setCategories((prev) => prev.map((c) => c.key === key ? { ...c, usefulLifeYears: years === '' ? '' : parseInt(years, 10) || 0 } : c));
  };

  const lifeInvalid = categories.some((c) => !(c.usefulLifeYears >= 1 && c.usefulLifeYears <= 50));
  const thresholdInvalid = threshold === '' || Number(threshold) < 0;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      // Only what changed is sent; a new life applies to assets bought afterwards
      const changedCats = categories.filter((c) => saved.categories.find((s) => s.key === c.key)?.usefulLifeYears !== c.usefulLifeYears);
      const body = {};
      if (Number(threshold) !== Number(saved.capitalizationThreshold)) body.capitalizationThreshold = threshold;
      if (changedCats.length) body.categories = changedCats.map((c) => ({ key: c.key, usefulLifeYears: c.usefulLifeYears }));
      if (Object.keys(body).length) {
        const res = await fixedAssetService.saveConfig(body);
        if (res?.data) apply(res.data);
      }
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(serverMessage(err, 'Failed to save asset settings'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 2 }}><Typography>Loading...</Typography></Box>;

  return (
    <Box sx={{ p: 2, maxWidth: 500 }}>
      <Typography sx={{ fontSize: fs.md, fontWeight: fw.semibold, mb: 2 }}>Asset Settings</Typography>

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 1.5 }}>Settings saved</Alert>}
      {!canEdit && <Alert severity="info" sx={{ mb: 1.5, py: 0 }}>Only the typing center admin can change these settings.</Alert>}

      {/* Threshold */}
      <Paper sx={{ p: 2, mb: 2, border: '1px solid', borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }}>
        <Typography sx={{ fontSize: fs.sm, fontWeight: fw.medium, mb: 1 }}>Capitalization Threshold</Typography>
        <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', mb: 1 }}>
          Items below this amount are expensed immediately instead of being capitalized as assets.
        </Typography>
        <TextField
          size="small" type="number" value={threshold}
          onChange={(e) => setThreshold(e.target.value)}
          InputProps={{ startAdornment: <Typography sx={{ fontSize: fs.xs, mr: 0.5, color: 'text.secondary' }}>AED</Typography> }}
          sx={{ width: 160 }}
          inputProps={{ min: 0, step: '0.01' }}
          disabled={!canEdit}
          error={thresholdInvalid}
        />
      </Paper>

      {/* Category Useful Life */}
      <Paper sx={{ p: 2, mb: 2, border: '1px solid', borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }}>
        <Typography sx={{ fontSize: fs.sm, fontWeight: fw.medium, mb: 0.5 }}>Category Useful Life</Typography>
        <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', mb: 1 }}>
          1 to 50 years. A change applies to assets bought afterwards; existing assets keep their life.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {categories.map((cat) => (
            <Box key={cat.key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: fs.sm }}>{cat.label}</Typography>
              <TextField
                size="small" type="number" value={cat.usefulLifeYears}
                onChange={(e) => updateCatLife(cat.key, e.target.value)}
                InputProps={{ endAdornment: <Typography sx={{ fontSize: fs.xs, ml: 0.5, color: 'text.secondary' }}>years</Typography> }}
                sx={{ width: 120 }}
                inputProps={{ min: 1, max: 50 }}
                disabled={!canEdit}
                error={!(cat.usefulLifeYears >= 1 && cat.usefulLifeYears <= 50)}
              />
            </Box>
          ))}
        </Box>
      </Paper>

      {canEdit && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" size="small" onClick={handleSave} disabled={saving || lifeInvalid || thresholdInvalid} sx={{ textTransform: 'none' }}>
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default AssetSettings;
